import React from "react";
import { observer, Observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import ContentNutritionRecipesStore from "stores/ContentNutritionRecipesStore";
import FoodMealTypeDictionary from "stores/dictionary/FoodMealTypeDictionary";

import Search from "components/Input/Search";
import FormControl from "components/FormControl";
import LoadingButton from "components/LoadingButton";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import CardListFieldControl from "components/FormControl/CardListFieldControl";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { TableSortType } from "stores/templates/TableStore";

import { FILTER_COLUMNS } from "constants/filterConstants";
import { PUBLISH_STATUSES } from "constants/models";

import {
  Container,
  Row,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  TitleContainer,
  SubtitleContainer,
  CardListRow,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type WeekRecipe = {
  foodMealTypeId: number;
  recipeIds: number[];
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  shouldShowUpdateConfirmation: boolean;
  categoryId?: number;
  isWeekCategory?: boolean;
  existingWeekRecipes?: WeekRecipe[];
} & SnackbarContextProps;

const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class RecipeForm extends React.Component<Props> {
  @observable initialValues: any = {};
  @observable updateConfirmationIsOpened: boolean = false;
  @observable chosenMealTypeId: number | null = null;

  @computed get customOption() {
    return FoodMealTypeDictionary.options.find(
      ({ label }) => label === "Custom",
    );
  }

  @computed get hasMoreInfo() {
    return ContentNutritionRecipesStore.hasMoreInfo;
  }

  @computed get searchValue() {
    return ContentNutritionRecipesStore.search;
  }

  @computed get options() {
    return ContentNutritionRecipesStore.recipesOptions;
  }

  @computed get filteredOptions() {
    const { isWeekCategory, existingWeekRecipes } = this.props;

    if (
      !isWeekCategory ||
      !existingWeekRecipes?.length ||
      !this.chosenMealTypeId
    ) {
      return this.options;
    }

    const [mealTypeRecipes] = existingWeekRecipes.filter(
      ({ foodMealTypeId }) => foodMealTypeId === this.chosenMealTypeId,
    );

    return this.options.map(option => ({
      ...option,
      isDisabled: mealTypeRecipes?.recipeIds?.includes(option.value.id),
    }));
  }

  @computed get foodMealTypeLabels() {
    return FoodMealTypeDictionary.options.map(({ label }) => label);
  }

  componentDidMount() {
    const { isWeekCategory, categoryId } = this.props;

    if (!isWeekCategory) {
      ContentNutritionRecipesStore.applyFilter({
        column: "NotIncludeInFoodCategoryId",
        value: categoryId,
      });
    }
    ContentNutritionRecipesStore.fetch();
    this.calcInitialValues();
  }

  componentWillUnmount() {
    ContentNutritionRecipesStore.reset();
  }

  calcInitialValues = () => {
    const { isWeekCategory } = this.props;

    const [recipeLabelOption] = FoodMealTypeDictionary.options;

    this.initialValues =
      isWeekCategory && recipeLabelOption
        ? { recipeLabel: recipeLabelOption.value }
        : {};
    this.chosenMealTypeId = recipeLabelOption.value;
  };

  fetchMoreRecipes = async () => {
    const { setMessage } = this.props;

    try {
      await ContentNutritionRecipesStore.fetchMoreRecipes();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentNutritionRecipesStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentNutritionRecipesStore.fetch();
  };

  onSearchClear = () => {
    ContentNutritionRecipesStore.clearSearch();
    ContentNutritionRecipesStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentNutritionRecipesStore.applyOrder(value);
    ContentNutritionRecipesStore.resetPage();
    ContentNutritionRecipesStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentNutritionRecipesStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentNutritionRecipesStore.resetPage();
    ContentNutritionRecipesStore.fetch();
  };

  onFilterClear = () => {
    ContentNutritionRecipesStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    ContentNutritionRecipesStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    ContentNutritionRecipesStore.fetch();
  };

  onChangeIsDirty = ({ dirty, values }: { dirty: boolean; values: any }) => {
    const { onChangeIsDirty } = this.props;

    if (Object.keys(values).length === 1) {
      onChangeIsDirty({ dirty: false });
      return;
    }

    onChangeIsDirty({ dirty });
  };

  onChangeFoodTypeCallback = (newValue: string, previousValue: string) => {
    if (previousValue !== newValue && this.searchValue) {
      ContentNutritionRecipesStore.resetPage();
      this.onSearchClear();
    }

    this.chosenMealTypeId = Number.parseInt(newValue);
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit, shouldShowUpdateConfirmation } = this.props;

    const publishedRecipesSelected = values.workouts?.length
      ? values.workouts?.some(({ publishStatus }: { publishStatus: string }) =>
          [
            PUBLISH_STATUSES.PUBLISHED,
            PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
          ].includes(publishStatus),
        )
      : Object.values(values).some(
          (value: any) =>
            Array.isArray(value) &&
            value.some(({ publishStatus }: { publishStatus: string }) =>
              [
                PUBLISH_STATUSES.PUBLISHED,
                PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
              ].includes(publishStatus),
            ),
        );

    shouldShowUpdateConfirmation && publishedRecipesSelected
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  render() {
    const { onClose, isWeekCategory } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy
          subscription={{ dirty: true, values: true }}
          onChange={this.onChangeIsDirty}
        />
        <Container>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const selectedItemsCount = !isWeekCategory
                ? values?.recipes?.length
                : Object.keys(values)
                    .filter(key => this.foodMealTypeLabels.includes(key))
                    .reduce(
                      (itemsCount, key) => itemsCount + values[key].length,
                      0,
                    );

              return (
                <Row>
                  <TitleContainer>
                    <Typography variant="h1" color="textPrimary">
                      Add Recipe
                    </Typography>
                    {!!selectedItemsCount && (
                      <SubtitleContainer>
                        <Typography variant="subtitle1" color="textPrimary">
                          {`${selectedItemsCount} items selected`}
                        </Typography>
                      </SubtitleContainer>
                    )}
                  </TitleContainer>
                </Row>
              );
            }}
          </FormSpy>

          {isWeekCategory && (
            <Row>
              <DropdownFieldControl
                name="recipeLabel"
                label="Meal Type"
                provider={FoodMealTypeDictionary}
                onChangeCallback={this.onChangeFoodTypeCallback}
                required
              />
            </Row>
          )}

          <Row>
            <Search
              initValue={ContentNutritionRecipesStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={
                    ContentNutritionRecipesStore.orderContext.appliedOrderInfo
                  }
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={ContentNutritionRecipesStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <CardListRow id="scrollableDiv" isWeekCategory={isWeekCategory}>
            {!ContentNutritionRecipesStore.isLoading ? (
              <FormSpy subscription={{ values: true }}>
                {({ values }) => {
                  const [fieldName] = FoodMealTypeDictionary.options.filter(
                    ({ value }) => value === values.recipeLabel,
                  );

                  return (
                    <Observer>
                      {() => (
                        <CardListFieldControl
                          name={
                            isWeekCategory
                              ? values.recipeLabel
                                ? `${fieldName.label}`
                                : `${fieldName?.label}`
                              : "recipes"
                          }
                          options={this.filteredOptions}
                          hasMore={this.hasMoreInfo}
                          fetchMore={this.fetchMoreRecipes}
                          searchValue={this.searchValue}
                          noItemsPlaceholderText={"No recipes added yet."}
                          scrollableTarget="scrollableDiv"
                          multi
                          withInfinityScroll
                        />
                      )}
                    </Observer>
                  );
                }}
              </FormSpy>
            ) : (
              <Loader />
            )}
          </CardListRow>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      Add
    </LoadingButton>
  );
}

export default withGlobalSnackbar(RecipeForm);
